import { useMemo } from 'react';
import {
  type PreviewSettings,
  type Orientation,
  type PageSize,
  type PageBreak,
  type ColumnWidth,
  type TextOverflow,
  type GroupExpansion,
  type MetadataVisibility,
} from '../types';

import FontSizeSlider from './FontSizeSlider';
import { ReportingPeriodSettings } from './ReportingPeriodSettings';
import { type SoqlFilter } from 'common/components/FilterBar/SoqlFilter';
import {
  isTablePrintingImprovementForFiltersEnabled,
  isTablePrintingImprovementGroupExpansionEnabled,
  isTablePrintingImprovementHideElementsEnabled,
  isTablePrintingImprovementHideGridlinesEnabled,
  isTablePrintingImprovementInheritGroupExpansionEnabled,
} from 'common/visualizations/helpers/VifSelectors';

interface SettingsProps {
  settings: PreviewSettings;
  onChange: (settings: PreviewSettings) => void;
  filters?: SoqlFilter[];
}

export const Settings = ({ settings, onChange, filters }: SettingsProps) => {
  const handleChange = <K extends keyof PreviewSettings>(key: K, value: PreviewSettings[K]) => {
    onChange({ ...settings, [key]: value });
  };
  const isInherit = settings.columnWidth === 'inherit';

  const dateFilters = useMemo(() => {
    return (
      filters?.filter(
        (filter) =>
          filter.dataTypeName === 'date' &&
          filter.arguments != null &&
          Object.keys(filter.arguments || {}).length > 0,
      ) || []
    );
  }, [filters]);

  const renderPageBreak = () => {
    if (settings.template !== 'grouped-aggregate') {
      return;
    }

    return (
      <div className="pdf-preview-modal__settings-group">
        <label>Page break</label>
        <select
          data-testid="pdf-page-break"
          value={settings.pageBreak}
          onChange={(e) => handleChange('pageBreak', e.target.value as PageBreak)}
        >
          <option value="standard" data-testid="pdf-page-break-standard">
            Standard
          </option>
          <option value="group" data-testid="pdf-page-break-group">
            Break on groups
          </option>
        </select>
      </div>
    );
  };

  return (
    <div className="pdf-preview-modal__settings">
      {/* <div className="pdf-preview-modal__settings-group">
        <label>Template</label>
        <select
          value={settings.template}
          onChange={(e) => handleChange('template', e.target.value as TemplateType)}
        >
          <option value="transaction-table">Transaction Table</option>
          <option value="income-statement">Income Statement</option>
          <option value="grouped-aggregate">Aggregated</option>
        </select>
      </div> */}

      <div className="pdf-preview-modal__settings-group">
        <label>Page size</label>
        <select
          data-testid="pdf-page-size"
          value={settings.pageSize}
          onChange={(e) => handleChange('pageSize', e.target.value as PageSize)}
        >
          <option value="A4" data-testid="pdf-page-size-a4">
            A4
          </option>
          <option value="Letter" data-testid="pdf-page-size-letter">
            Letter
          </option>
          <option value="Legal" data-testid="pdf-page-size-legal">
            Legal
          </option>
        </select>
      </div>

      <div className="pdf-preview-modal__settings-group">
        <label>Orientation</label>
        <select
          data-testid="pdf-orientation"
          value={settings.orientation}
          onChange={(e) => handleChange('orientation', e.target.value as Orientation)}
        >
          <option value="portrait" data-testid="pdf-orientation-portrait">
            Portrait
          </option>
          <option value="landscape" data-testid="pdf-orientation-landscape">
            Landscape
          </option>
        </select>
      </div>

      {renderPageBreak()}

      <div className="pdf-preview-modal__settings-group">
        <label>Column width</label>
        <select
          data-testid="pdf-column-width"
          value={settings.columnWidth}
          onChange={(e) => handleChange('columnWidth', e.target.value as ColumnWidth)}
        >
          <option value="bestfit" data-testid="pdf-column-width-best-fit">
            Best fit
          </option>
          <option value="inherit" data-testid="pdf-column-width-inherit">
            Inherit
          </option>
        </select>
      </div>

      {isInherit && <FontSizeSlider onChange={handleChange} fontSize={settings.fontSize} />}

      <div className="pdf-preview-modal__settings-group">
        <label>Text overflow</label>
        <select
          data-testid="pdf-text-overflow"
          value={settings.textOverflow}
          onChange={(e) => handleChange('textOverflow', e.target.value as TextOverflow)}
        >
          <option value="linebreak" data-testid="pdf-text-overflow-line-break">
            Word Wrap
          </option>
          <option value="visible" data-testid="pdf-text-overflow-visible">
            Overflow
          </option>
          <option value="ellipsize" data-testid="pdf-text-overflow-ellipsize">
            Truncate
          </option>
        </select>
      </div>

      {/* Group expansion config: */}
      {isTablePrintingImprovementGroupExpansionEnabled() && settings.groupExpansion && (
        <div className="pdf-preview-modal__settings-group">
          <label>Group expansion</label>
          <select
            value={settings.groupExpansion}
            onChange={(e) => handleChange('groupExpansion', e.target.value as GroupExpansion)}
            data-testid="group-expansion"
          >
            <option value="expanded" data-testid="group-expansion-expanded">
              Expanded
            </option>
            <option value="collapsed" data-testid="group-expansion-collapsed">
              Collapsed
            </option>
            {isTablePrintingImprovementInheritGroupExpansionEnabled() && (
              <option value="inherit" data-testid="group-expansion-inherit">
                Match report state
              </option>
            )}
          </select>
        </div>
      )}

      {isTablePrintingImprovementForFiltersEnabled() && (
        <ReportingPeriodSettings
          dateFilters={dateFilters}
          reportingPeriod={settings.reportingPeriod}
          onChange={handleChange}
        />
      )}

      {isTablePrintingImprovementHideElementsEnabled() && (
        <div className="pdf-preview-modal__settings-group">
          <label>Metadata</label>
          <select
            data-testid="pdf-metadata-visibility"
            value={settings.metadataVisibility}
            onChange={(e) => handleChange('metadataVisibility', e.target.value as MetadataVisibility)}
          >
            <option value="all" data-testid="pdf-metadata-visibility-all">
              Show all
            </option>
            <option value="partial" data-testid="pdf-metadata-visibility-partial">
              Show partial
            </option>
            <option value="hide" data-testid="pdf-metadata-visibility-hide">
              Hide
            </option>
          </select>
        </div>
      )}

      {(isTablePrintingImprovementHideGridlinesEnabled() ||
        isTablePrintingImprovementHideElementsEnabled()) && (
        <div className="pdf-preview-modal__settings-group pdf-preview-modal__settings-checkboxes">
          {isTablePrintingImprovementHideGridlinesEnabled() && (
            <label>
              <input
                type="checkbox"
                checked={settings.showGridlines !== false}
                onChange={(e) => handleChange('showGridlines', e.target.checked)}
                data-testid="pdf-show-gridlines"
              />
              Show gridlines
            </label>
          )}
          {isTablePrintingImprovementHideElementsEnabled() && (
            <label>
              <input
                type="checkbox"
                checked={settings.showLogo !== false}
                onChange={(e) => handleChange('showLogo', e.target.checked)}
                data-testid="pdf-show-logo"
              />
              Show logo
            </label>
          )}
        </div>
      )}
    </div>
  );
};

export default Settings;
